import React from "react";
import { dash, formatDDMMYYYY, getDayMonthYear, englishOrdinalSuffix } from "@/lib/documentUtils";
import AutoFitLine from "@/components/AutoFitLine";

/**
 * Vakalatnama — latest layout:
 *   • Extras appear only in the body line (Name / MAH / Advocate Code)
 *     with NO numeric prefix and NO contact details.
 *   • Client signature area is completely blank (no label, no name).
 *   • Main advocate's Address / E-mail / Mobile sits in a bordered box at
 *     the END of the page (after the client signature area).
 */
export const Vakalatnama = React.forwardRef(function Vakalatnama({ data }, ref) {
  const {
    caseNo, courtName, courtPlace,
    party1Name, party2Name, party1Role, party2Role,
    advocateName, advocateMahNumber, advocateCode, advocateAddress,
    advocateMobile, advocateEmail,
    extraAdvocates = [], date,
    advocateAppearingFor, advocateAppearingNumber,
  } = data;

  const appearLabel = `${dash(advocateAppearingFor, "Appellant")}${
    advocateAppearingNumber && String(advocateAppearingNumber).trim().length
      ? ` No. ${String(advocateAppearingNumber).trim()}`
      : ""
  }`;
  const { day, month, yearWords } = getDayMonthYear(date);
  const filedDate = formatDDMMYYYY(date);

  const activeExtras = extraAdvocates.filter(
    (a) => a.name || a.mah || a.code,
  );

  return (
    <div ref={ref} className="a4-sheet vakalat-sheet" data-testid="vakalatnama-doc">
      <div className="welfare-note-vakalat">
        Under Section 34(1) of Advocate Act 1961 and Bombay High Court, Appellate Side,
        Rules Schedules 7/1985. I am not a member of Advocate&apos;s Welfare Fund, hence a
        Welfare fund stamp is not affixed.
      </div>

      <h1 className="doc-title vakalat-title">VAKALATNAMA</h1>

      <div className="memo-case-row">
        <div className="memo-case-spacer" />
        <div className="memo-case-no-right" data-testid="vakalat-case-no">
          {dash(caseNo, "Reg. _____________ No. ____ / _______")}
        </div>
      </div>

      <h2 className="doc-court-heading">IN THE COURT OF {dash(courtName, "_____________________")}</h2>
      <h3 className="doc-court-at">AT- {dash(courtPlace, "_____________________")}</h3>

      <div className="party-block">
        <div className="pb-row">
          <div className="pb-name">{dash(party1Name, "____________________")}</div>
          <div className="pb-brace">{"}"}</div>
          <div className="pb-role">
            Appellant / Applicant
            <br />
            Complainant / Petitioner
          </div>
        </div>
        <div className="pb-vs">V/s</div>
        <div className="pb-row">
          <div className="pb-name">{dash(party2Name, "____________________")}</div>
          <div className="pb-brace">{"}"}</div>
          <div className="pb-role">
            Respondent / Opponent
            <br />
            Accused / Defendant
          </div>
        </div>
      </div>

      <div className="doc-body vakalat-body">
        <p>
          I / We the undersigned, the <strong>{appearLabel}</strong> abovenamed, hereby
          appoint and authorize:
        </p>

        <p className="adv-line">
          Adv. <strong>{dash(advocateName, "_________________________")}</strong>. (MAH:{" "}
          {dash(advocateMahNumber, "________/____")}) (Advocate Code:{" "}
          {dash(advocateCode, "____")})
        </p>

        {activeExtras.map((a, idx) => (
          <p className="adv-line" key={idx}>
            Adv. <strong>{dash(a.name, "_____________________")}</strong>. (MAH: {dash(a.mah, "________/____")})
            {" "}(Advocate Code: {dash(a.code, "____")})
          </p>
        ))}

        <p>
          to act, appear and plead on my / our behalf in the above matter and to conduct,
          prosecute and defend.
        </p>

        <p>
          In witness whereof I / We have signed below this{" "}
          <strong>{day}{englishOrdinalSuffix(day)}</strong>&nbsp;Day of{" "}
          <strong>{month}</strong> in the year <strong>{yearWords}</strong>.
        </p>
      </div>

      {/* Accepted & Filed (advocate side) — client side is intentionally blank */}
      <div className="vakalat-accept-row">
        <div>
          <div className="filed-line">Accepted &amp; Filed&nbsp;&nbsp;{filedDate}</div>
          <div className="adv-sig-label">Adv.</div>
        </div>
        <div className="signatory-block" data-testid="vakalat-client-signature">
          <div className="sig-line" aria-hidden="true" />
        </div>
      </div>

      {/* === Main Advocate Contact box — bottom of the page, plain white === */}
      <div className="adv-contact-box adv-contact-plain" data-testid="vakalat-advocate-contact">
        <div className="adv-contact-grid">
          <div className="adv-contact-full">
            <span className="adv-info-label">Address:</span>{" "}
            <span className="adv-info-value">
              {dash(advocateAddress, "_______________________________________________")}
            </span>
          </div>
          <AutoFitLine className="adv-contact-full" testId="vakalat-email-mobile-line">
            <span className="adv-em-cell">
              <span className="adv-info-label">E-mail:</span>{" "}
              <span className="adv-info-value">{dash(advocateEmail, "____________________")}</span>
            </span>
            <span className="adv-em-cell">
              <span className="adv-info-label">Mobile:</span>{" "}
              <span className="adv-info-value">{dash(advocateMobile, "____________")}</span>
            </span>
          </AutoFitLine>
        </div>
      </div>
    </div>
  );
});

export default Vakalatnama;
